import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { Add, BookmarkBorder, Close as CloseIcon, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import { Button, FieldWrapper, PageHeader, SectionHeader, selectLoadingProps, StepActions } from "@components/ui_v2";
import ControlledPlaceAutocompleteV2 from "@components/ControlledPlaceAutocompleteV2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetGuaranteeConfigQuery,
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import {
  useDeleteGuaranteesTypesMutation,
  useLazyGetGuaranteeTypeDetailConfigQuery,
  useLazyGetGuaranteesDataQuery,
  usePatchGuaranteesTypesMutation,
  usePostGuaranteesTypesMutation,
} from "@api/garantiasApi";
import { usePostValidateNextStageMutation, usePutApplicationCompletedMutation } from "@api/applications";
import { GuaranteeConfigStruture, GuaranteeVehicleDetailConfig, VehicleStrutureCatalog } from "@interfaces/catalogs";
import { ApiError, GarantiasVehicularInmobiliariaResponse } from "@interfaces/response";
import { PayloadRequestGarantiasVehicularInmobiliaria } from "@interfaces/request";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";
import {
  cleanAmount,
  detailNameOf,
  emptyGarantia,
  EMPTY_VEHICLE_CODES,
  formKindOf,
  mapInmobiliariaGuarantee,
  mapVehicularGuarantee,
  MAX_GUARANTEES,
  onlyDigits,
  selectActiveGuarantees,
  VEHICULOS_HORAS_USO,
  VEHICULOS_MANUAL,
} from "@helpers/informacionMultiGarantiasHelpers";
import { GarantiaFormValue, VehicleCatalogCodes } from "@interfaces/informacionMultiGarantias";
import { formatCurrencyInput } from "@helpers/funciones";

const SCREEN_STATUS = STATUS_CATALOG.informacion_garantia.code;

const FIELD_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: "12px 14px",
} as const;

/** Replica `.fs-la-tag`: pill informativo sobre la valuación del bien prendario. */
const ValuacionTag = ({ muted, children }: { muted?: boolean; children: ReactNode }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      mt: "4px",
      mb: "14px",
      px: "11px",
      py: "6px",
      borderRadius: "20px",
      fontSize: 11.5,
      fontWeight: 600,
      bgcolor: muted ? "tokens.surface2" : "primary.base",
      color: muted ? "text.disabled" : "primary.main",
    }}
  >
    {children}
  </Box>
);

export const InformacionMultiGarantias = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(SCREEN_STATUS);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [getGuaranteeTypeDetailConfig] = useLazyGetGuaranteeTypeDetailConfigQuery();
  const [triggerListVehicleYears] = useLazyGetVehiclesYearsQuery();
  const [triggerGetVehicleByYear] = useLazyGetVehiclesByYearQuery();
  const [triggerGetVehicleByYearAndBrand] = useLazyGetVehiclesByYearAndMakeQuery();
  const [triggerGetVehicleByYearBrandAndModel] = useLazyGetVehiclesByYearMakeAndModelQuery();
  const [postGuarantees] = usePostGuaranteesTypesMutation();
  const [patchGuarantees] = usePatchGuaranteesTypesMutation();
  const [deleteGuarantees] = useDeleteGuaranteesTypesMutation();
  const [putApplicationGuarantees] = usePutApplicationCompletedMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [guaranteeConfigList, setGuaranteeConfigList] = useState<GuaranteeConfigStruture[]>([]);
  const [detailConfigByType, setDetailConfigByType] = useState<Record<string, GuaranteeVehicleDetailConfig[]>>({});
  const [listYears, setListYears] = useState<string[]>([]);

  // Catálogos encadenados de Libro Azul, por garantía (`localId`).
  const [brandsOptions, setBrandsOptions] = useState<Record<string, VehicleStrutureCatalog[]>>({});
  const [modelsOptions, setModelsOptions] = useState<Record<string, VehicleStrutureCatalog[]>>({});
  const [versionsOptions, setVersionsOptions] = useState<Record<string, VehicleStrutureCatalog[]>>({});
  const [loadingBrand, setLoadingBrand] = useState<Record<string, boolean>>({});
  const [loadingModel, setLoadingModel] = useState<Record<string, boolean>>({});
  const [loadingVersion, setLoadingVersion] = useState<Record<string, boolean>>({});

  // TODO: el legacy nunca pone `isLoading` en true antes de su carga inicial (solo lo apaga en el
  // `finally`), así que el skeleton inicial nunca se ve. Se replica el mismo comportamiento; el
  // select de tipo igual queda deshabilitado porque `canInteract` exige catálogo cargado.
  const [isLoading, setIsLoading] = useState(false);
  /** Carga de catálogos dentro de una garantía (año → marca → modelo → versión): solo deshabilita campos. */
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [isDeletingOnTypeChange, setIsDeletingOnTypeChange] = useState(false);

  const [pendingTypeChange, setPendingTypeChange] = useState<{
    localId: string;
    nextGuaranteeTypeId: string;
  } | null>(null);

  // Caches vivos para las funciones async: evitan releer state obsoleto dentro de una misma cadena.
  const detailConfigRef = useRef<Record<string, GuaranteeVehicleDetailConfig[]>>({});
  const yearsRef = useRef<string[]>([]);

  const ensureDetailConfig = async (guaranteeTypeId: string) => {
    if (!guaranteeTypeId) return [];
    const cached = detailConfigRef.current[guaranteeTypeId];
    if (cached) return cached;

    const details = await getGuaranteeTypeDetailConfig({
      guarantee_type_id: guaranteeTypeId,
      taxcode: application.type,
    }).unwrap();

    detailConfigRef.current = { ...detailConfigRef.current, [guaranteeTypeId]: details };
    setDetailConfigByType(detailConfigRef.current);
    return details;
  };

  const ensureVehicleYears = async () => {
    if (yearsRef.current.length > 0) return yearsRef.current;
    const years = await triggerListVehicleYears().unwrap();
    yearsRef.current = years;
    setListYears(years);
    return years;
  };

  const formKind = (guaranteeTypeId: string) => formKindOf(guaranteeConfigList, guaranteeTypeId);
  const detailName = (guaranteeTypeId: string, detailId: string) =>
    detailNameOf(detailConfigByType[guaranteeTypeId], detailId);

  // El esquema se reconstruye cuando llegan los catálogos porque las ramas dependen de ellos
  // (qué tipo de garantía es y si el detalle prendario es manual o de Libro Azul).
  const validationSchema = useMemo(() => {
    const isInmobiliaria = (guaranteeTypeId: string) =>
      formKindOf(guaranteeConfigList, guaranteeTypeId) === "inmobiliaria";
    const isVehicular = (guaranteeTypeId: string) => formKindOf(guaranteeConfigList, guaranteeTypeId) === "vehicular";
    const isManual = (guaranteeTypeId: string, detailId: string) =>
      VEHICULOS_MANUAL.includes(detailNameOf(detailConfigByType[guaranteeTypeId], detailId));

    const requiredWhen = (predicate: (guaranteeTypeId: string) => boolean) =>
      yup.string().when("guarantee_type_id", {
        is: (guaranteeTypeId: string) => predicate(guaranteeTypeId),
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      });

    return yup.object().shape({
      garantias: yup.array().of(
        yup.object().shape({
          guarantee_type_id: yup.string().required("Campo requerido"),
          // Inmobiliaria
          type_inmobiliaria: requiredWhen(isInmobiliaria),
          valor_estimado: requiredWhen(isInmobiliaria),
          ubicacion: yup
            .string()
            .when("guarantee_type_id", {
              is: (guaranteeTypeId: string) => isInmobiliaria(guaranteeTypeId),
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            })
            .test(
              "has-coordinates",
              "Selecciona una dirección válida desde el listado o ubícala en el mapa",
              (value, ctx) => {
                const parent = ctx.parent as Partial<GarantiaFormValue> | undefined;
                if (!isInmobiliaria(parent?.guarantee_type_id ?? "")) return true;
                if (!value) return true;

                const lat = Number(parent?.latitud);
                const lng = Number(parent?.longitud);
                return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
              },
            ),
          // Vehicular / prendaria
          type_vehicle: requiredWhen(isVehicular),
          year: requiredWhen(isVehicular),
          brand: requiredWhen(isVehicular),
          model: requiredWhen(isVehicular),
          version: requiredWhen(isVehicular),
          km: requiredWhen(isVehicular),
          estimated_value: yup.string().when(["guarantee_type_id", "type_vehicle"], {
            is: (guaranteeTypeId: string, detailId: string) =>
              isVehicular(guaranteeTypeId) && isManual(guaranteeTypeId, detailId),
            then: (schema) => schema.required("Campo requerido"),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      ),
    });
  }, [guaranteeConfigList, detailConfigByType]);

  const buildGuaranteePayload = (item: GarantiaFormValue): PayloadRequestGarantiasVehicularInmobiliaria | null => {
    const kind = formKind(item.guarantee_type_id);

    if (kind === "inmobiliaria") {
      return {
        loan_application_id: application.id,
        guarantee_type_id: item.guarantee_type_id,
        guarantee_type_detail_id: item.type_inmobiliaria,
        estimated_value: cleanAmount(item.valor_estimado),
        latitude: Number(item.latitud),
        longitude: Number(item.longitud),
        address: item.ubicacion,
      };
    }

    if (kind === "vehicular") {
      const vehicleType = detailName(item.guarantee_type_id, item.type_vehicle);
      const manual = VEHICULOS_MANUAL.includes(vehicleType);

      return {
        loan_application_id: application.id,
        guarantee_type_id: item.guarantee_type_id,
        estimated_value: manual ? cleanAmount(item.estimated_value) : null,
        guarantee_type_detail_id: item.type_vehicle,
        details: {
          vehicle_type: vehicleType || "",
          guarantee_type_detail_id: manual ? null : item.type_vehicle,
          year: Number(item.year) || 0,
          make: item.brand_value || "",
          model: item.model_value || "",
          version: item.version_value || "",
          car_version_code: manual ? null : String(item.version),
          estimated_mileage: cleanAmount(item.km),
        },
      };
    }

    // Tipos sin formulario (ej. cuentas por cobrar): no se guarda garantía, solo viaja en `guarantee_types`.
    return null;
  };

  const formik = useFormik<{ garantias: GarantiaFormValue[] }>({
    initialValues: { garantias: [emptyGarantia()] },
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        // Cada garantía se guarda por separado: PATCH si ya existe en backend, POST si es nueva.
        for (const item of values.garantias) {
          const payload = buildGuaranteePayload(item);
          if (!payload) continue;

          if (item.guaranteeId) {
            await patchGuarantees({
              loan_application_id: application.id,
              guarantee_id: item.guaranteeId,
              data: payload,
            }).unwrap();
          } else {
            await postGuarantees({
              loan_application_id: application.id,
              data: { guarantees_list: [payload] },
            }).unwrap();
          }
        }

        const guaranteeTypes = Array.from(
          new Set(values.garantias.map((item) => item.guarantee_type_id).filter(Boolean)),
        );

        await putApplicationGuarantees({
          id_sol: application.id,
          body: { guarantee_types: guaranteeTypes },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus.url || "");
      } catch (error) {
        Sentry.captureException(error, {
          extra: { component: "InformacionMultiGarantias-submit", values: formik.values },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const garantias = formik.values.garantias;

  const fieldError = (index: number, field: keyof GarantiaFormValue): string => {
    const errors = formik.errors.garantias as Array<Partial<Record<keyof GarantiaFormValue, string>>> | undefined;
    const touched = formik.touched.garantias as Array<Partial<Record<keyof GarantiaFormValue, boolean>>> | undefined;

    const message = errors?.[index]?.[field];
    return touched?.[index]?.[field] && message ? message : "";
  };

  const setItemField = (index: number, field: keyof GarantiaFormValue, value: string) =>
    formik.setFieldValue(`garantias[${index}].${field}`, value);

  const dropItemCatalogs = (localId: string) => {
    const drop = (prev: Record<string, unknown>) => {
      const next = { ...prev };
      delete next[localId];
      return next;
    };
    setBrandsOptions((prev) => drop(prev) as Record<string, VehicleStrutureCatalog[]>);
    setModelsOptions((prev) => drop(prev) as Record<string, VehicleStrutureCatalog[]>);
    setVersionsOptions((prev) => drop(prev) as Record<string, VehicleStrutureCatalog[]>);
  };

  /**
   * Resuelve los catálogos encadenados del Libro Azul para una garantía existente y los deja
   * cargados en los selects. Devuelve los códigos que el mapeo puro necesita.
   */
  const loadVehicleCatalogCodes = async (
    guarantee: GarantiasVehicularInmobiliariaResponse,
    localId: string,
  ): Promise<VehicleCatalogCodes> => {
    const year = guarantee.details?.year?.toString() || "";
    if (!year) return EMPTY_VEHICLE_CODES;

    const brandCatalog = await triggerGetVehicleByYear({ year }).unwrap();
    setBrandsOptions((prev) => ({ ...prev, [localId]: brandCatalog }));
    const brand = brandCatalog.find((item) => item.name === guarantee.details?.make)?.code || "";
    if (!brand) return EMPTY_VEHICLE_CODES;

    const modelCatalog = await triggerGetVehicleByYearAndBrand({ year, make: brand }).unwrap();
    setModelsOptions((prev) => ({ ...prev, [localId]: modelCatalog }));
    const model = modelCatalog.find((item) => item.name === guarantee.details?.model)?.code || "";
    if (!model) return { brand, model: "" };

    const versionCatalog = await triggerGetVehicleByYearBrandAndModel({ year, make: brand, model }).unwrap();
    setVersionsOptions((prev) => ({ ...prev, [localId]: versionCatalog }));

    return { brand, model };
  };

  /** Reconstruye una garantía existente del backend: carga sus catálogos y aplica el mapeo puro. */
  const hydrateGuarantee = async (
    guarantee: GarantiasVehicularInmobiliariaResponse,
    config: GuaranteeConfigStruture[],
  ): Promise<GarantiaFormValue> => {
    const guaranteeTypeId = guarantee.guarantee_type_id ?? "";
    const base: GarantiaFormValue = {
      ...emptyGarantia(),
      guaranteeId: guarantee.id,
      guarantee_type_id: guaranteeTypeId,
    };

    const kind = formKindOf(config, guaranteeTypeId);
    if (!kind) return base;

    const details = await ensureDetailConfig(guaranteeTypeId);
    if (kind === "inmobiliaria") return mapInmobiliariaGuarantee(guarantee, base);

    await ensureVehicleYears();

    const manual = VEHICULOS_MANUAL.includes(String(guarantee.details?.vehicle_type || ""));
    const codes = manual ? EMPTY_VEHICLE_CODES : await loadVehicleCatalogCodes(guarantee, base.localId);

    return mapVehicularGuarantee(guarantee, details, codes, base);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = await getGuaranteeTypeConfig({ tax_code: application.type }).unwrap();
        const guaranteeData = await getGuaranteesData({ loan_application_id: application.id }).unwrap();

        setGuaranteeConfigList(config);

        const activeGuarantees = selectActiveGuarantees(guaranteeData);
        if (activeGuarantees.length === 0) return;

        const items = await Promise.all(activeGuarantees.map((item) => hydrateGuarantee(item, config)));
        formik.setValues({ garantias: items });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { component: "InformacionMultiGarantias-initial-data", values: { applicationId: application.id } },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guaranteeTypeOptions = useMemo(
    () =>
      guaranteeConfigList.map((item) => ({
        id: item.guarantee_type_id,
        name: item.guarantee_type.name,
        code: item.guarantee_type.code,
      })),
    [guaranteeConfigList],
  );

  const canInteract = !isLoading && guaranteeTypeOptions.length > 0;
  const isBlocked = isLoading || isLoadingCatalogs || formik.isSubmitting || isDeletingOnTypeChange;

  /** Aplica el cambio de tipo: limpia la garantía y precarga los catálogos del tipo nuevo. */
  const applyGuaranteeTypeChange = async (localId: string, guaranteeTypeId: string) => {
    const index = garantias.findIndex((item) => item.localId === localId);
    if (index === -1) return;

    dropItemCatalogs(localId);
    await formik.setFieldValue(`garantias[${index}]`, {
      ...emptyGarantia(),
      localId,
      guarantee_type_id: guaranteeTypeId,
    });

    try {
      setIsLoadingCatalogs(true);
      await ensureDetailConfig(guaranteeTypeId);
      if (formKind(guaranteeTypeId) === "vehicular") {
        await ensureVehicleYears();
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "InformacionMultiGarantias-type-change", values: { guaranteeTypeId } },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
    }
  };

  const requestGuaranteeTypeChange = (localId: string, nextGuaranteeTypeId: string) => {
    if (formik.isSubmitting || isDeletingOnTypeChange) return;

    const item = garantias.find((garantia) => garantia.localId === localId);
    if (!item || nextGuaranteeTypeId === item.guarantee_type_id) return;

    // Si la garantía ya existe en backend, cambiar de tipo la elimina: se confirma antes.
    if (item.guaranteeId) {
      setPendingTypeChange({ localId, nextGuaranteeTypeId });
      return;
    }

    applyGuaranteeTypeChange(localId, nextGuaranteeTypeId);
  };

  const handleCloseTypeChangeDialog = () => {
    if (isDeletingOnTypeChange) return;
    setPendingTypeChange(null);
  };

  const handleConfirmTypeChange = async () => {
    if (!pendingTypeChange) return;

    const item = garantias.find((garantia) => garantia.localId === pendingTypeChange.localId);
    const backendGuaranteeId = item?.guaranteeId;

    // Por seguridad: si ya no hay id, solo cambia el tipo.
    if (!backendGuaranteeId) {
      applyGuaranteeTypeChange(pendingTypeChange.localId, pendingTypeChange.nextGuaranteeTypeId);
      setPendingTypeChange(null);
      return;
    }

    setIsDeletingOnTypeChange(true);
    try {
      await deleteGuarantees({
        loan_application_id: application.id,
        guarantee_id: backendGuaranteeId,
      }).unwrap();

      await applyGuaranteeTypeChange(pendingTypeChange.localId, pendingTypeChange.nextGuaranteeTypeId);
      setPendingTypeChange(null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionMultiGarantias-change-guarantee-type",
          values: {
            localId: pendingTypeChange.localId,
            backendGuaranteeId,
            nextGuaranteeTypeId: pendingTypeChange.nextGuaranteeTypeId,
          },
        },
      });

      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      // Mantener el tipo actual (no se cambia nada)
      setPendingTypeChange(null);
    } finally {
      setIsDeletingOnTypeChange(false);
    }
  };

  const handleAddGarantia = () => {
    if (formik.isSubmitting || isDeletingOnTypeChange) return;
    if (garantias.length >= MAX_GUARANTEES) return;
    formik.setFieldValue("garantias", [...garantias, emptyGarantia()]);
  };

  const handleRemoveGarantia = async (localId: string) => {
    if (formik.isSubmitting || isDeletingOnTypeChange) return;

    const item = garantias.find((garantia) => garantia.localId === localId);
    if (!item) return;

    let canDelete = true;

    if (item.guaranteeId) {
      setIsLoading(true);
      try {
        await deleteGuarantees({
          loan_application_id: application.id,
          guarantee_id: item.guaranteeId,
        }).unwrap();
      } catch (error) {
        canDelete = false;

        Sentry.captureException(error, {
          extra: {
            component: "InformacionMultiGarantias-remove-guarantee",
            values: { localId, backendGuaranteeId: item.guaranteeId },
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    }

    if (!canDelete) return;

    dropItemCatalogs(localId);
    const next = garantias.filter((garantia) => garantia.localId !== localId);
    formik.setFieldValue("garantias", next.length > 0 ? next : [emptyGarantia()]);
  };

  const handleYearChange = async (index: number, value: string) => {
    const { localId } = garantias[index];
    try {
      setIsLoadingCatalogs(true);
      setLoadingBrand((prev) => ({ ...prev, [localId]: true }));
      setLoadingModel((prev) => ({ ...prev, [localId]: true }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: true }));

      setItemField(index, "year", value);

      const brands = value ? await triggerGetVehicleByYear({ year: value }).unwrap() : [];

      setBrandsOptions((prev) => ({ ...prev, [localId]: brands }));
      setModelsOptions((prev) => ({ ...prev, [localId]: [] }));
      setVersionsOptions((prev) => ({ ...prev, [localId]: [] }));

      setItemField(index, "brand", "");
      setItemField(index, "brand_value", "");
      setItemField(index, "model", "");
      setItemField(index, "model_value", "");
      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
      setLoadingBrand((prev) => ({ ...prev, [localId]: false }));
      setLoadingModel((prev) => ({ ...prev, [localId]: false }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: false }));
    }
  };

  const handleBrandChange = async (index: number, value: string) => {
    const { localId, year } = garantias[index];
    try {
      setIsLoadingCatalogs(true);
      setLoadingModel((prev) => ({ ...prev, [localId]: true }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: true }));

      const selectedBrand = brandsOptions[localId]?.find((brand) => brand.code === value);
      setItemField(index, "brand", value);
      setItemField(index, "brand_value", selectedBrand?.name || "");

      const models = value ? await triggerGetVehicleByYearAndBrand({ year, make: value }).unwrap() : [];

      setModelsOptions((prev) => ({ ...prev, [localId]: models }));
      setVersionsOptions((prev) => ({ ...prev, [localId]: [] }));

      setItemField(index, "model", "");
      setItemField(index, "model_value", "");
      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
      setLoadingModel((prev) => ({ ...prev, [localId]: false }));
      setLoadingVersion((prev) => ({ ...prev, [localId]: false }));
    }
  };

  const handleModelChange = async (index: number, value: string) => {
    const { localId, year, brand } = garantias[index];
    try {
      setIsLoadingCatalogs(true);
      setLoadingVersion((prev) => ({ ...prev, [localId]: true }));

      const selectedModel = modelsOptions[localId]?.find((model) => model.code === value);
      setItemField(index, "model", value);
      setItemField(index, "model_value", selectedModel?.name || "");

      const versions = await triggerGetVehicleByYearBrandAndModel({ year, make: brand, model: value }).unwrap();

      setVersionsOptions((prev) => ({ ...prev, [localId]: versions }));

      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingCatalogs(false);
      setLoadingVersion((prev) => ({ ...prev, [localId]: false }));
    }
  };

  const handleVersionChange = (index: number, value: string) => {
    const { localId } = garantias[index];
    const selectedVersion = versionsOptions[localId]?.find((version) => version.code === value);
    setItemField(index, "version", value);
    setItemField(index, "version_value", selectedVersion?.name || "");
  };

  /** Al cambiar de tipo prendario se limpia todo si cambia el modo de captura (manual ↔ Libro Azul). */
  const handleVehicleTypeChange = (index: number, value: string) => {
    const item = garantias[index];
    const wasManual = VEHICULOS_MANUAL.includes(detailName(item.guarantee_type_id, item.type_vehicle));
    const willBeManual = VEHICULOS_MANUAL.includes(detailName(item.guarantee_type_id, value));

    setItemField(index, "type_vehicle", value);

    if (wasManual !== willBeManual) {
      dropItemCatalogs(item.localId);
      setItemField(index, "year", "");
      setItemField(index, "brand", "");
      setItemField(index, "brand_value", "");
      setItemField(index, "model", "");
      setItemField(index, "model_value", "");
      setItemField(index, "version", "");
      setItemField(index, "version_value", "");
      setItemField(index, "km", "");
      setItemField(index, "estimated_value", "");
    }
  };

  const handleBack = () => {
    if (previousStatus) navigate(previousStatus.url);
  };

  const isContinueDisabled =
    !canInteract ||
    isBlocked ||
    garantias.some((item) => !item.guarantee_type_id) ||
    !formik.isValid ||
    formik.isSubmitting;

  const renderGarantia = (item: GarantiaFormValue, index: number) => {
    const testId = (name: string) => `garantia-${index + 1}-${name}`;
    const kind = formKind(item.guarantee_type_id);
    const details = detailConfigByType[item.guarantee_type_id] ?? [];
    const vehicleType = detailName(item.guarantee_type_id, item.type_vehicle);
    const manual = VEHICULOS_MANUAL.includes(vehicleType);
    const horasUso = VEHICULOS_HORAS_USO.includes(vehicleType);
    const isLoadingBrand = Boolean(loadingBrand[item.localId]);
    const isLoadingModel = Boolean(loadingModel[item.localId]);
    const isLoadingVersion = Boolean(loadingVersion[item.localId]);

    return (
      <>
        <FieldWrapper label="Tipo de garantía" full error={fieldError(index, "guarantee_type_id")}>
          <TextField
            autoComplete="off"
            size="lendia_input"
            select
            fullWidth
            value={item.guarantee_type_id}
            onBlur={() => formik.setFieldTouched(`garantias[${index}].guarantee_type_id`, true)}
            onChange={(event) => requestGuaranteeTypeChange(item.localId, event.target.value)}
            disabled={!canInteract || isBlocked}
            inputProps={{ "data-testid": testId("tipo-select") }}
          >
            <MenuItem value="">Selecciona…</MenuItem>
            {guaranteeTypeOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </FieldWrapper>

        {kind === "inmobiliaria" && (
          <>
            <SectionHeader label="Detalle de tu garantía inmobiliaria" />
            <Box sx={FIELD_GRID_SX}>
              <FieldWrapper label="Tipo de inmueble" full error={fieldError(index, "type_inmobiliaria")}>
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  select
                  fullWidth
                  value={item.type_inmobiliaria}
                  onBlur={() => formik.setFieldTouched(`garantias[${index}].type_inmobiliaria`, true)}
                  onChange={(event) => setItemField(index, "type_inmobiliaria", event.target.value)}
                  disabled={isBlocked}
                  inputProps={{ "data-testid": testId("inmueble-select") }}
                >
                  <MenuItem value="">Selecciona…</MenuItem>
                  {details.map((detail) => (
                    <MenuItem key={detail.guarantee_type_detail_id} value={detail.guarantee_type_detail_id}>
                      {detail.guarantee_type_detail.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FieldWrapper>

              <FieldWrapper label="Valor estimado" full error={fieldError(index, "valor_estimado")}>
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  fullWidth
                  value={item.valor_estimado}
                  onBlur={() => formik.setFieldTouched(`garantias[${index}].valor_estimado`, true)}
                  // TODO: paridad con el legacy inmobiliario — al vaciar el campo queda "0" (el
                  // formulario vehicular sí lo deja vacío). Confirmar cuál comportamiento se queda.
                  onChange={(event) =>
                    setItemField(
                      index,
                      "valor_estimado",
                      Number(onlyDigits(event.target.value)).toLocaleString("en-US"),
                    )
                  }
                  placeholder="2,000,000"
                  disabled={isBlocked}
                  inputProps={{ inputMode: "numeric", maxLength: 20, "data-testid": testId("valor-input") }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </FieldWrapper>

              <FieldWrapper
                label="Ubicación del bien"
                hint="Autocompletado con Google Maps."
                full
                error={fieldError(index, "ubicacion")}
              >
                <ControlledPlaceAutocompleteV2
                  label={null}
                  value={{
                    address: item.ubicacion,
                    lat: Number(item.latitud) || 0,
                    lng: Number(item.longitud) || 0,
                  }}
                  onChange={(newValue) => {
                    const isFromSelectionOrMap = newValue.lat !== 0 || newValue.lng !== 0;

                    if (isFromSelectionOrMap) {
                      formik.setValues(
                        {
                          garantias: garantias.map((garantia, garantiaIndex) =>
                            garantiaIndex !== index
                              ? garantia
                              : {
                                  ...garantia,
                                  ubicacion: newValue.address,
                                  latitud: String(newValue.lat),
                                  longitud: String(newValue.lng),
                                },
                          ),
                        },
                        true,
                      );

                      formik.setFieldTouched(`garantias[${index}].ubicacion`, true, false);
                      return;
                    }

                    // Tipeo manual: limpia coords para que no pase la validación.
                    formik.setFieldValue(`garantias[${index}].ubicacion`, newValue.address, false);
                    formik.setFieldValue(`garantias[${index}].latitud`, "", false);
                    formik.setFieldValue(`garantias[${index}].longitud`, "", false);
                  }}
                  onBlur={() => formik.setFieldTouched(`garantias[${index}].ubicacion`, true)}
                  error={Boolean(fieldError(index, "ubicacion"))}
                  disabled={isBlocked}
                />
              </FieldWrapper>
            </Box>
          </>
        )}

        {kind === "vehicular" && (
          <>
            <Box sx={{ ...FIELD_GRID_SX, mt: "14px" }}>
              <FieldWrapper label="Tipo de garantía prendaria" full error={fieldError(index, "type_vehicle")}>
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  select
                  fullWidth
                  value={item.type_vehicle}
                  onBlur={() => formik.setFieldTouched(`garantias[${index}].type_vehicle`, true)}
                  onChange={(event) => handleVehicleTypeChange(index, event.target.value)}
                  disabled={isBlocked}
                  inputProps={{ "data-testid": testId("tipo-prendario-select") }}
                >
                  <MenuItem value="">Selecciona…</MenuItem>
                  {details.map((detail) => (
                    <MenuItem key={detail.guarantee_type_detail_id} value={detail.guarantee_type_detail_id}>
                      {detail.guarantee_type_detail.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FieldWrapper>
            </Box>

            {!!item.type_vehicle && !manual && (
              <>
                <ValuacionTag>
                  <BookmarkBorder sx={{ fontSize: 13 }} />
                  <Typography variant="body2">Valuación automática con Libro Azul</Typography>
                </ValuacionTag>

                <Box sx={FIELD_GRID_SX}>
                  <FieldWrapper label="Año" error={fieldError(index, "year")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      select
                      fullWidth
                      value={item.year}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].year`, true)}
                      onChange={(event) => handleYearChange(index, event.target.value)}
                      disabled={isBlocked}
                      inputProps={{ "data-testid": testId("anio-select") }}
                    >
                      <MenuItem value="">Selecciona…</MenuItem>
                      {listYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FieldWrapper>

                  <FieldWrapper label="Marca" loading={isLoadingBrand} error={fieldError(index, "brand")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      select
                      fullWidth
                      value={item.brand}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].brand`, true)}
                      onChange={(event) => handleBrandChange(index, event.target.value)}
                      disabled={isBlocked || isLoadingBrand}
                      SelectProps={selectLoadingProps(isLoadingBrand)}
                      inputProps={{ "data-testid": testId("marca-select") }}
                    >
                      <MenuItem value="">Selecciona…</MenuItem>
                      {(brandsOptions[item.localId] ?? []).map((brand) => (
                        <MenuItem key={brand.code} value={brand.code}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FieldWrapper>

                  <FieldWrapper label="Modelo" loading={isLoadingModel} error={fieldError(index, "model")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      select
                      fullWidth
                      value={item.model}
                      disabled={isBlocked || isLoadingModel || !item.brand}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].model`, true)}
                      onChange={(event) => handleModelChange(index, event.target.value)}
                      SelectProps={selectLoadingProps(isLoadingModel)}
                      inputProps={{ "data-testid": testId("modelo-select") }}
                    >
                      <MenuItem value="">{item.brand ? "Selecciona…" : "Elige marca"}</MenuItem>
                      {(modelsOptions[item.localId] ?? []).map((model) => (
                        <MenuItem key={model.code} value={model.code}>
                          {model.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FieldWrapper>

                  <FieldWrapper label="Versión" loading={isLoadingVersion} error={fieldError(index, "version")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      select
                      fullWidth
                      value={item.version}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].version`, true)}
                      onChange={(event) => handleVersionChange(index, event.target.value)}
                      disabled={isBlocked || isLoadingVersion || !item.model}
                      SelectProps={selectLoadingProps(isLoadingVersion)}
                      inputProps={{ "data-testid": testId("version-select") }}
                    >
                      <MenuItem value="">Selecciona…</MenuItem>
                      {(versionsOptions[item.localId] ?? []).map((version) => (
                        <MenuItem key={version.code} value={version.code}>
                          {version.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FieldWrapper>

                  <FieldWrapper label="Kilometraje estimado" full error={fieldError(index, "km")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.km}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].km`, true)}
                      onChange={(event) => setItemField(index, "km", formatCurrencyInput(event.target.value))}
                      placeholder="20,000"
                      disabled={isBlocked}
                      inputProps={{ inputMode: "numeric", maxLength: 11, "data-testid": testId("km-input") }}
                      InputProps={{ endAdornment: <InputAdornment position="end">Km</InputAdornment> }}
                    />
                  </FieldWrapper>
                </Box>
              </>
            )}

            {!!item.type_vehicle && manual && (
              <>
                <ValuacionTag muted>
                  <InfoOutlined sx={{ fontSize: 13 }} />
                  <Typography variant="body2">Este tipo no tiene valuación automática; captura los datos.</Typography>
                </ValuacionTag>

                <Box sx={FIELD_GRID_SX}>
                  <FieldWrapper label="Año" error={fieldError(index, "year")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.year}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].year`, true)}
                      onChange={(event) => {
                        if (/^\d{0,4}$/.test(event.target.value)) setItemField(index, "year", event.target.value);
                      }}
                      placeholder="Ej. 2021"
                      disabled={isBlocked}
                      inputProps={{ maxLength: 4, inputMode: "numeric", "data-testid": testId("anio-input") }}
                    />
                  </FieldWrapper>

                  <FieldWrapper label="Marca" error={fieldError(index, "brand")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.brand}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].brand`, true)}
                      onChange={(event) => {
                        setItemField(index, "brand", event.target.value);
                        setItemField(index, "brand_value", event.target.value);
                      }}
                      placeholder="Marca"
                      disabled={isBlocked}
                      inputProps={{ "data-testid": testId("marca-input") }}
                    />
                  </FieldWrapper>

                  <FieldWrapper label="Modelo" error={fieldError(index, "model")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.model}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].model`, true)}
                      onChange={(event) => {
                        setItemField(index, "model", event.target.value);
                        setItemField(index, "model_value", event.target.value);
                      }}
                      placeholder="Modelo"
                      disabled={isBlocked}
                      inputProps={{ "data-testid": testId("modelo-input") }}
                    />
                  </FieldWrapper>

                  <FieldWrapper label="Versión" error={fieldError(index, "version")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.version}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].version`, true)}
                      onChange={(event) => {
                        setItemField(index, "version", event.target.value);
                        setItemField(index, "version_value", event.target.value);
                      }}
                      placeholder="Versión"
                      disabled={isBlocked}
                      inputProps={{ "data-testid": testId("version-input") }}
                    />
                  </FieldWrapper>

                  <FieldWrapper
                    label={horasUso ? "Horas de uso/Km" : "Kilometraje estimado"}
                    error={fieldError(index, "km")}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.km}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].km`, true)}
                      onChange={(event) =>
                        setItemField(
                          index,
                          "km",
                          // Maquinaria amarilla / montacargas se capturan sin separador de miles.
                          horasUso ? onlyDigits(event.target.value) : formatCurrencyInput(event.target.value),
                        )
                      }
                      placeholder="20,000"
                      disabled={isBlocked}
                      inputProps={{ inputMode: "numeric", maxLength: 11, "data-testid": testId("km-input") }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{horasUso ? "Hrs/Km" : "Km"}</InputAdornment>,
                      }}
                    />
                  </FieldWrapper>

                  <FieldWrapper label="Valor factura" error={fieldError(index, "estimated_value")}>
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      value={item.estimated_value}
                      onBlur={() => formik.setFieldTouched(`garantias[${index}].estimated_value`, true)}
                      onChange={(event) =>
                        setItemField(index, "estimated_value", formatCurrencyInput(event.target.value))
                      }
                      placeholder="0"
                      disabled={isBlocked}
                      inputProps={{ inputMode: "numeric", maxLength: 20, "data-testid": testId("valor-factura-input") }}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </FieldWrapper>
                </Box>
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Garantía"
        title="Información de tu garantía"
        subtitle="Este crédito es con garantía. Indícanos el tipo y los datos del bien que la respalda."
      />

      {isLoading ? (
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: "16px", mb: "14px" }} />
      ) : (
        <>
          {garantias.map((item, index) => (
            <Paper key={item.localId} variant="lendia_paper" sx={{ mb: "14px" }}>
              {garantias.length > 1 && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
                    Garantía {index + 1}
                  </Typography>
                  <Button
                    tone="ghost"
                    startIcon={<DeleteOutline sx={{ fontSize: 13 }} />}
                    onClick={() => handleRemoveGarantia(item.localId)}
                    disabled={!canInteract || isBlocked}
                    data-testid={`garantia-${index + 1}-quitar-button`}
                    sx={{ px: 0.75, py: 0.5, fontSize: 12, fontWeight: 400, color: "text.secondary" }}
                  >
                    Quitar
                  </Button>
                </Box>
              )}

              {renderGarantia(item, index)}
            </Paper>
          ))}

          <Button
            tone="ghost"
            startIcon={<Add sx={{ fontSize: 13 }} />}
            onClick={handleAddGarantia}
            disabled={!canInteract || isBlocked || garantias.length >= MAX_GUARANTEES}
            data-testid="garantia-agregar-button"
            sx={{
              border: "1px dashed",
              borderColor: "tokens.borderStrong",
              bgcolor: "background.paper",
              color: "primary.main",
              fontSize: 13,
              px: 2,
              py: 1.25,
              "&:hover": { borderColor: "primary.main", bgcolor: "primary.base" },
            }}
          >
            Agregar otra garantía
          </Button>
        </>
      )}

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        nextDisabled={isContinueDisabled}
        backTestId="garantia-back-button"
        nextTestId="garantia-continue-button"
      />

      <Dialog
        open={pendingTypeChange !== null}
        onClose={handleCloseTypeChangeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", color: "primary.dark", position: "relative", pr: 6, pt: 3 }}>
          Cambiar tipo de garantía
          <IconButton
            aria-label="Cerrar"
            onClick={handleCloseTypeChangeDialog}
            disabled={isDeletingOnTypeChange}
            sx={{ position: "absolute", right: 8, top: 8, color: "primary.dark" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
            Se eliminará esta garantía al cambiar el tipo de esta garantía.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
            <Button
              tone="ghost"
              fullWidth
              onClick={handleCloseTypeChangeDialog}
              disabled={isDeletingOnTypeChange}
              data-testid="garantia-cambiar-tipo-cancel-button"
            >
              Regresar
            </Button>
            <Button
              fullWidth
              onClick={handleConfirmTypeChange}
              disabled={isDeletingOnTypeChange}
              data-testid="garantia-cambiar-tipo-submit-button"
            >
              Cambiar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
